<script lang="ts">
  import { Plus, X } from 'lucide-svelte';
  import ListProducts from '../components/ListProducts.svelte';
  import ListProductTypes from '../components/ListProductTypes.svelte';
  import ListCategories from '../components/ListCategories.svelte';
  import Modal from '../components/ui/Modal.svelte';
  import AddProductToRoomForm from '../components/forms/AddProductToRoomForm.svelte';
  import AddProductForm from '../components/forms/AddProductForm.svelte';

  // The catalogue is mirrored read-only from ProQuote (the master catalog) and kept in
  // sync automatically. Existing products are edited/priced there. Designers can still add
  // a genuinely-missing product here — it's created in ProQuote (pending review) and mirrored
  // straight back — so a missing SKU never blocks a design.
  let showAddToRoomDialog = $state(false);
  let showAddProductDialog = $state(false);

  type Tab = 'products' | 'types' | 'categories';
  let activeTab: Tab = $state('products');

  const tabs: { id: Tab; label: string }[] = [
    { id: 'products', label: 'Products' },
    { id: 'types', label: 'Product types' },
    { id: 'categories', label: 'Categories' }
  ];
</script>

<div>
  <div class="flex flex-wrap items-center justify-between gap-3 mb-2">
    <div class="inline-flex rounded border border-border-default p-0.5 text-xs" role="tablist">
      {#each tabs as tab}
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === tab.id}
          class="px-2.5 py-1 rounded transition-colors duration-100 {activeTab === tab.id
            ? 'bg-text-primary text-bg-base'
            : 'bg-bg-hover text-text-secondary hover:text-text-primary'}"
          onclick={() => (activeTab = tab.id)}
        >
          {tab.label}
        </button>
      {/each}
    </div>

    {#if activeTab === 'products'}
      <div class="flex gap-2">
        <button
          type="button"
          onclick={() => (showAddProductDialog = true)}
          class="btn-secondary inline-flex items-center gap-1.5 text-sm"
        >
          <Plus class="w-3.5 h-3.5" />
          Add new product
        </button>
        <button
          type="button"
          onclick={() => (showAddToRoomDialog = true)}
          class="btn-primary inline-flex items-center gap-1.5 text-sm"
        >
          <Plus class="w-3.5 h-3.5" />
          Add product to room
        </button>
      </div>
    {/if}
  </div>

  <p class="text-xs text-text-secondary mb-4">
    Synced from ProQuote. Browse and assign to rooms; if something's genuinely missing, use
    “Add new product” — it's created in ProQuote (pending review) and appears here right away.
  </p>

  {#if activeTab === 'products'}
    <Modal
      bind:open={showAddToRoomDialog}
      size="md"
      onclose={() => (showAddToRoomDialog = false)}
    >
      {#snippet header()}
        <div class="flex items-center justify-between w-full">
          <h2 class="text-xl font-semibold text-text-primary">Add Product to Room</h2>
          <button
            type="button"
            class="p-2 bg-bg-hover hover:bg-bg-hover rounded-lg transition"
            aria-label="Close"
            onclick={() => { showAddToRoomDialog = false; }}
          >
            <X class="w-5 h-5" />
          </button>
        </div>
      {/snippet}
      {#snippet children()}
        <AddProductToRoomForm
          onsuccess={() => (showAddToRoomDialog = false)}
          oncancel={() => (showAddToRoomDialog = false)}
        />
      {/snippet}
    </Modal>
    <Modal
      bind:open={showAddProductDialog}
      size="md"
      onclose={() => (showAddProductDialog = false)}
    >
      {#snippet header()}
        <div class="flex items-center justify-between w-full">
          <h2 class="text-xl font-semibold text-text-primary">Add New Product</h2>
          <button
            type="button"
            class="p-2 bg-bg-hover hover:bg-bg-hover rounded-lg transition"
            aria-label="Close"
            onclick={() => { showAddProductDialog = false; }}
          >
            <X class="w-5 h-5" />
          </button>
        </div>
      {/snippet}
      {#snippet children()}
        <AddProductForm
          onsuccess={() => (showAddProductDialog = false)}
          oncancel={() => (showAddProductDialog = false)}
        />
      {/snippet}
    </Modal>
    <ListProducts />
  {:else if activeTab === 'types'}
    <ListProductTypes />
  {:else}
    <ListCategories />
  {/if}
</div>
